import { z } from 'zod';

import { DateFormat, SelectOptionColors, TimeFormat } from './field-settings.ts';

import { type ADFDocument } from '@canvas/adf-types';

import {
  type BlogpostReference,
  type ContentState,
  type DATABASE_CONTENT_TYPE,
  type OptionalCreatePageReference,
  spaceReferenceValidator,
  type TemplateReference,
  templateReferenceValidator,
  type User,
  type UserReference
} from '../confluence/types.ts';
import {
  type ExceptCreate,
  type ObjectMode,
  type OnlyCreate,
  type OnlyExisting,
  type Optional,
  type SerializedError
} from '../types.ts';
import {
  booleanStringValidator,
  jsonStringValidator,
  transformValidator
} from '../validators.ts';

import { aiFieldConfigValidator } from './ai-field-config.ts';
import { type DatabaseView } from './view/types.ts';

export {
  aiFieldConfigValidator,
  aiSummaryFieldConfigValidator,
  aiSummaryOutputFormatValidator
} from './ai-field-config.ts';
export type {
  AiFieldConfig,
  AiSummaryFieldConfig,
  AiSummaryOutputFormat
} from './ai-field-config.ts';

export type Database<Mode extends ObjectMode = ObjectMode.EXISTING> = {
  /** A random ID for the database. Note that while this should be unique, it cannot be guaranteed that it is. */
  id: string;
  type: typeof DATABASE_CONTENT_TYPE;
  space: { key: string } & OnlyExisting<Mode, { name: string; id: number }>;
  name: string;
  fields: string[];
  fieldsById: Record<string, DatabaseField>;
  /** A virtual entry that contains the default values for all fields. This entry is not listed in the entries/entriesById properties. */
  defaultEntry: DatabaseEntry;
  entries: string[];
  entriesById: Record<string, DatabaseEntry>;
  meta: DatabaseMeta;
  /**
   * The cover image of the database. A DatabaseAttachment indicates that the cover image is already attached and does
   * not need to be changed. A File object indicates that the file should be uploaded. undefined indicates that the database
   * should have no cover image.
   */
  coverImage: DatabaseAttachment | File | undefined;
  views: DatabaseView[];
  defaultViewId: string;
  commentThreads: Record<string, Array<DatabaseCommentThread>>;
} & OnlyExisting<
  Mode,
  {
    createdDate: string;
    createdBy: User;
    lastUpdatedDate: string | undefined;
    lastUpdatedBy: User;
    operations: DatabaseOperations;
  }
> &
  ExceptCreate<
    Mode,
    {
      /**
       * The ID of the database CCEO. Because Confluence guarantees that this is unique, it should
       * be used whenever possible as a unique identifier for the database.
       */
      contentId: string;

      /**
       * The canvas document ID.
       */
      referenceId?: string;

      /**
       * The current content revision of the database CCEO. Confluence requires this during content updates
       * to prevent conflicts when multiple people are editing the same content at the same time.
       */
      contentVersion: number;

      /** The ID of the database entries CCEO. */
      entriesContentId: string;
      /** The content revision of the database entries CCEO. */
      entriesContentVersion: number;

      /** The content revision of the meta property. */
      metaVersion: number;
    }
  > &
  OnlyCreate<
    Mode,
    {
      /** In create mode, this may contain a virtual content ID in order to be identified in the redux store. */
      contentId?: string;
    }
  >;

/**
 * A database without its entries.
 * This should be used in places where access to the entries is not needed, as it greatly improves the memoization
 * possibilities. A database object with entries changes every time the user types in something into any value, whereas
 * a database object without entries only changes when the database settings or field settings are changed.
 */
export type DatabaseWithoutEntries<
  Mode extends ObjectMode = ObjectMode.EXISTING,
  Db extends Database<Mode> = Database<Mode>
> = Omit<Db, 'entries' | 'entriesById'>;

/**
 * The common base of Database and BrokenDatabase. Contains all the data that is available straight from the document CCEO, regardless of
 * whether its content is malformatted.
 */
export type BasicDatabase = Pick<
  Database,
  | 'contentId'
  | 'contentVersion'
  | 'space'
  | 'name'
  | 'createdDate'
  | 'createdBy'
  | 'lastUpdatedDate'
  | 'lastUpdatedBy'
  | 'coverImage'
  | 'operations'
  | 'type'
>;

/**
 * A database without its content. Contains all the data that is available without expanding the document entries CCEO body and attachments.
 * Note that the "attachments" properties will only contain the attachments of the database CCEO, not the database entries CCEO.
 */
export type ShallowDatabase = Omit<
  Database,
  'entries' | 'entriesById' | 'meta' | 'metaVersion'
>;

/**
 * A database that could not be deserialized due to a validation error. Only parts of its data and an error
 * message are available.
 */
export type BrokenDatabase = BasicDatabase & {
  error: SerializedError;
};

/** Database metadata that is stored in a content property and can thus be updated separately from the database. */
export interface DatabaseMeta {
  /** A copy of the database ID for CQL indexing */
  id?: string;

  /** If this is true, the UI should prevent any changes to the database fields (adding/removing fields, changing field settings). */
  isLocked?: boolean;

  /** If this is 1, the database should be shown in database pickers in other spaces. */
  isGlobal?: 0 | 1;
}

/** An operation that the user has permission to do on a database. */
export interface DatabaseOperations {
  updateMetadata: boolean;
  updateEntries: boolean;
  delete: boolean;
}

/**
 * An attachment attached to a database.
 */
export interface DatabaseAttachment {
  containerId: string;
  attachmentId: string;
  filename: string;
  path: string;
}

export interface DatabaseField<Type extends DatabaseFieldType = DatabaseFieldType> {
  id: string;
  name: string;
  description?: string;
  showDescription?: boolean;
  type: Type;
  aiRunMetadata?: DatabaseAiFieldRunMetadata;

  /**
   * The width of this column in pixels. By default this is undefined. All available space is distributed equally
   * between all columns with undefined width, with a certain minimum width applied.
   */
  width?: number;

  /**
   * The settings of this field. Each field type specifies a different set of settings. Example fields could be
   * a minimum and maximum for a number field and a default parent page for a page field. All field settings are
   * optional and are persisted as macro parameters of an orderly-data macro, thus must be configured with the
   * right type in the macro configuration. When the field type is changed, the settings remain unmodified at first,
   * so the field settings may contain settings from any field type, not just the ones belonging to the actual type
   * of the field. However, when persisting the field, only those settings that belong to the current field type are
   * persisted.
   */
  settings: DatabaseFieldSettings;
}

/**
 * The settings of a database field. Each field type specifies a different set of settings. Example fields could be
 * a minimum and maximum for a number field and a default parent page for a page field. All field settings are
 * persisted as macro parameters of an orderly-data macro, thus must be configured with the right type in the macro
 * configuration. When the field type is changed, the settings remain unmodified, so the field settings may contain
 * settings from any field type, not just the ones belonging to the actual type of the field.
 */
export type DatabaseFieldSettings = z.infer<typeof fieldSettingsValidator>;

export enum DatabaseFieldType {
  TEXT = 'text',
  AI_SUMMARY = 'aiSummary',
  NUMBER = 'number',
  PAGE_LINK = 'page',
  ATTACHMENT = 'attachment',
  USER = 'user',
  DATE = 'date',
  CHECKBOX = 'checkbox',
  SELECT = 'select',
  HYPERLINK = 'hyperlink',
  JIRA_ISSUE = 'jira',
  ENTRY_LINK = 'databaseEntry',
  ENTRY_DETAILS = 'lookup',
  BACKLINK = 'backlink',
  PAGE_DETAILS = 'pageDetails',
  PAGE_LABELS = 'pageLabels',
  PAGE_STATUS = 'pageStatus',
  JIRA_ISSUE_DETAILS = 'jiraLookup'
}

export const databaseFieldTypeValidator = z.nativeEnum(DatabaseFieldType);

export type DatabaseEntry = {
  id: string;

  /**
   * The values of this entry, indexed by field ID. Usually a database entry should contain a value for each
   * existing field and no values for non-existing fields, but implementations should make sure that they ignore
   * values of non-existing fields and use the default value for fields whose value is missing.
   * The data type of each value may differ from the data type of its corresponding field, in case the field type
   * was changed after the value was created. Implementations should make sure to convert the value before displaying
   * it.
   * The getDatabaseValue() helper function can be used to retrieve database values and automatically takes care
   * of the data type conversion and the handling of missing values.
   */
  values: Record<string, DatabaseValue>;
};

/** The value of the id property of the default entry. */
export const DEFAULT_ENTRY_ID = 'default';

/** If a user field is set to this value, it should be dynamically resolved to the current user upon saving. */
export const USER_VALUE_CURRENT = 'current';

export type ExtraContentId = { contentId: string };

export interface DatabaseTextValue {
  plain: string;
  rich?: ADFDocument;
}

export type DatabaseAiActor = 'ai' | 'user';

export type DatabaseAiFieldGenerationFingerprint =
  | {
      fingerprintVersion: 1;
      sourceValueHash: string;
    }
  | {
      fingerprintVersion: 2;
      sourceValueHash: string;
      configurationHash: string;
    };

export interface DatabaseAiValueMetadata {
  lastUpdatedAt?: string;
  lastUpdatedBy?: DatabaseAiActor;
  lastUpdatedUserAccountId?: string;
  generation?: DatabaseAiFieldGenerationFingerprint;
}

export interface DatabaseAiFieldRunMetadata {
  lastRunAt?: string;
  lastRunBy?: DatabaseAiActor;
  lastRunUserAccountId?: string;
}

export interface DatabaseAiTextValue extends DatabaseTextValue {
  metadata?: DatabaseAiValueMetadata;
}

export function isDatabaseTextValue(value: unknown): value is DatabaseTextValue {
  return typeof value === 'object' && value !== null && 'plain' in value;
}

export type RawDatabaseValue<
  Type extends DatabaseFieldType = DatabaseFieldType,
  ExtraPageReferenceType = object
> = {
  [DatabaseFieldType.TEXT]: DatabaseTextValue;
  [DatabaseFieldType.AI_SUMMARY]: DatabaseAiTextValue;
  [DatabaseFieldType.NUMBER]: string;
  [DatabaseFieldType.CHECKBOX]: boolean;
  [DatabaseFieldType.PAGE_LINK]: Array<
    (
      | OptionalCreatePageReference<ExtraPageReferenceType>
      | BlogpostReference
      | { contentId: string; ari?: string | undefined } // Imported page link, will be resolved to a page reference in the UI and when saving OR page reference from collab database
      | object
    ) & {
      // Newly added value in a multi page field, not filled out yet
      /** A random ID to identify the page throughout the UI, is not persisted. */
      _id: string;
    }
  >;

  [DatabaseFieldType.ATTACHMENT]: Array<
    (
      | DatabaseAttachment // Existing database attachment
      | File // Newly uploaded file, will be attached to the database on saving
      | { src: string } // Newly imported file, will be attached to the database on saving
      | { fileId: string } // File hosted on Media API
      | undefined
    ) & {
      // Newly added value in a multi page field, not filled out yet
      /** A random ID to identify the attachment throughout the UI, is not persisted. */
      _id: string;
    }
  >;

  [DatabaseFieldType.USER]:
    | typeof USER_VALUE_CURRENT
    | Array<
        (UserReference | object) & {
          /** A random ID to identify the user reference throughout the UI, is not persisted. */
          _id: string;
        }
      >;

  [DatabaseFieldType.DATE]: string | undefined; // ISO date or "today"

  [DatabaseFieldType.SELECT]: string[]; // List of select option IDs

  [DatabaseFieldType.HYPERLINK]: Array<
    Hyperlink & {
      /** A random ID to identify the link throughout the UI, is not persisted. */
      _id: string;
    }
  >;

  [DatabaseFieldType.JIRA_ISSUE]: Array<
    (object | { key: string }) & {
      /** A random ID to identify the issue reference throughout the UI, is not persisted. */
      _id: string;
    }
  >;

  [DatabaseFieldType.ENTRY_LINK]: Array<
    (
      | object
      | {
          entryId: string;
          title?: string;
        }
    ) & {
      /** A random ID to identify the user reference throughout the UI, is not persisted. */
      _id: string;
    }
  >;

  [DatabaseFieldType.ENTRY_DETAILS]: undefined;

  [DatabaseFieldType.BACKLINK]: undefined;

  [DatabaseFieldType.PAGE_DETAILS]: Record<
    string,
    Optional<ContentState, 'id'> | undefined
  >;

  /** New label set by page key */
  [DatabaseFieldType.PAGE_LABELS]: Record<string, string[]>;

  /** New page status by page key, null for empty page status */
  [DatabaseFieldType.PAGE_STATUS]: Record<string, Optional<ContentState, 'id'> | null>;

  [DatabaseFieldType.JIRA_ISSUE_DETAILS]: undefined;
}[Type];

/**
 * A database value with its corresponding data type. The data type of a value does not always match the data
 * type of its field, in case the field type has been changed after the value was created. Implementations should
 * make sure to convert the value before displaying it.
 */
export type DatabaseValue<
  Type extends DatabaseFieldType = DatabaseFieldType,
  ExtraPageReferenceType = object
> = {
  [T in DatabaseFieldType]: {
    type: T;
    value: RawDatabaseValue<T, ExtraPageReferenceType>;
  };
}[Type];

export const databaseValueValidator = transformValidator(
  z.any(),
  (v) => {
    // Database values are persisted as part of "equals" filters in saved/custom views.
    // This means that we need to convert legacy format database values here.

    if (v.type === DatabaseFieldType.ENTRY_LINK && !Array.isArray(v.value)) {
      return {
        type: DatabaseFieldType.ENTRY_LINK,
        value: v.value?.entryRefs ?? []
      };
    }

    return v;
  },
  z.object({
    type: z.nativeEnum(DatabaseFieldType),
    value: z.any() // For now we don't validate the value. We can add this later when needed.
  })
) as z.ZodSchema<DatabaseValue>;

/** A database field with its corresponding database. */
export interface FieldWithContext<
  Type extends DatabaseFieldType = DatabaseFieldType,
  Mode extends ObjectMode = ObjectMode.EXISTING
> {
  database: DatabaseWithoutEntries<Mode>;
  field: DatabaseField<Type>;
}

/** A database entry with its corresponding database. */
export interface EntryWithContext<Mode extends ObjectMode = ObjectMode.EXISTING> {
  database: DatabaseWithoutEntries<Mode>;
  entry: DatabaseEntry;
}

/** A database value with its corresponding field, entry and database. */
export interface ValueWithContext<
  Type extends DatabaseFieldType = DatabaseFieldType,
  Mode extends ObjectMode = ObjectMode.EXISTING,
  FieldType extends DatabaseFieldType = DatabaseFieldType
> {
  database: DatabaseWithoutEntries<Mode>;
  field: DatabaseField<FieldType>;
  entry: DatabaseEntry;
  value: DatabaseValue<Type>;
}

/** A raw value with its corresponding field, entry and database. */
export interface RawValueWithContext<
  Type extends DatabaseFieldType = DatabaseFieldType,
  Mode extends ObjectMode = ObjectMode.EXISTING,
  FieldType extends DatabaseFieldType = DatabaseFieldType
> {
  database: DatabaseWithoutEntries<Mode>;
  field: DatabaseField<FieldType>;
  entry: DatabaseEntry;
  value: RawDatabaseValue<Type>;
}

export const databaseCreateOptionsValidator = z.object({
  name: z.string(),
  type: z.nativeEnum(DatabaseFieldType)
});

export type DatabaseCreateOptions = z.infer<typeof databaseCreateOptionsValidator>;

export const entryCreateOptionsValidator = z.object({
  _: z.any().optional() // Workaround because empty objects are broken
});

export type EntryCreateOptions = z.infer<typeof entryCreateOptionsValidator>;

export enum PageSubType {
  PAGE = 'PAGE',
  LIVE = 'LIVE'
}

export const pageSubTypeValidator = z.nativeEnum(PageSubType);

export enum MediaFormat {
  AUTOFILL = 'Autofill',
  THUMBNAIL = 'Thumbnail'
}

export const mediaFormatValidator = z.nativeEnum(MediaFormat);

/**
 * Defines which parent page new pages created by a page field should have.
 */
export enum ParentPageType {
  /**
   * Always create new pages under the page configured as "parentPage". If no parentPage is set, do not allow creating new pages.
   * This is the default setting.
   */
  PAGE = 'page',

  /**
   * @deprecated
   * When the database is being edited through a database macro, use the page that the macro is on as parent page. Otherwise
   * use ParentPageType.PAGE.
   */
  MACRO = 'macro',

  /**
   * @deprecated
   * Can only be set while creating a database through the create page editor (where the current page is still a draft and thus
   * cannot be configured as a parent page). When the macro is first rendered, will be replaced with ParentPageType.PAGE and the
   * parent page will be set to the current page.
   */
  AUTO = 'auto'
}

export enum ParentPageOption {
  DYNAMIC = 'dynamic',
  FIXED = 'fixed'
}

export const parentPageTypeValidator = z.nativeEnum(ParentPageType);

export const parentPageOptionValidator = z.nativeEnum(ParentPageOption);

// We used to have only blue, red, yellow, green, teal, purple, grey, greyLight, which represented the corresponding colour
// schemes of @atlaskit/tag. greyLight is a dark-on-light color pattern, grey is a light-on-dark color pattern, while all
// the others are kind of a dark-on-medium color pattern with bad readability. Because of this we decided to introduce
// pairs of dark-on-light and light-on-dark colors, matching the color schemes of @atlaskit/lozenge where possible.
// The legacy dark-on-medium color patterns (all except grey/greyLight) should be converted to the new light style.
const selectOptionLegacyColorConversionMap = {
  blue: 'blueLight',
  red: 'redLight',
  yellow: 'yellowLight',
  green: 'greenLight',
  teal: 'tealLight',
  purple: 'purpleLight',
  grey: 'greyDark'
} satisfies Record<string, (typeof SelectOptionColors)[number]>;

export const selectOptionColorsValidator = transformValidator(
  z.enum([
    ...SelectOptionColors,
    ...(Object.keys(selectOptionLegacyColorConversionMap) as Array<
      keyof typeof selectOptionLegacyColorConversionMap
    >)
  ]),
  (color) =>
    selectOptionLegacyColorConversionMap[
      color as keyof typeof selectOptionLegacyColorConversionMap
    ] ?? color,
  z.enum(SelectOptionColors)
);

/** A configured option for a select field. */
export const selectOptionValidator = z.object({
  /** Is undefined for legacy options. In that case the label should be used as ID. */
  id: z.string().optional(),
  label: z.string(),
  color: selectOptionColorsValidator
});

/** A configured option for a select field. */
export type SelectOption = z.infer<typeof selectOptionValidator>;

export enum NumberSeparators {
  COMMA = ',',
  DOT = '.',
  WHITESPACE = '\u202f',
  APOSTROPHE = "'"
}

export enum NumberFormats {
  NUMBER = 'number',
  PERCENT = 'percent',
  CUSTOM = 'custom',
  DOLLAR = 'USD',
  EURO = 'EUR',
  CURRENCY = 'currency'
}

export const predefinedNumberFormats: Record<NumberFormats, NumberFormat> = {
  [NumberFormats.NUMBER]: {
    format: NumberFormats.NUMBER,
    prefix: undefined,
    suffix: undefined,
    thousandsSeparator: undefined,
    decimalSeparator: NumberSeparators.DOT,
    decimalPlaces: undefined
  },
  [NumberFormats.CUSTOM]: {
    format: NumberFormats.CUSTOM,
    prefix: '',
    suffix: '',
    thousandsSeparator: undefined,
    decimalSeparator: NumberSeparators.DOT,
    decimalPlaces: undefined
  },
  [NumberFormats.PERCENT]: {
    format: NumberFormats.PERCENT,
    prefix: undefined,
    suffix: '%',
    thousandsSeparator: undefined,
    decimalSeparator: NumberSeparators.DOT,
    decimalPlaces: undefined
  },
  [NumberFormats.EURO]: {
    format: NumberFormats.EURO,
    prefix: undefined,
    suffix: '€',
    thousandsSeparator: NumberSeparators.WHITESPACE,
    decimalSeparator: NumberSeparators.DOT,
    decimalPlaces: undefined
  },
  [NumberFormats.DOLLAR]: {
    format: NumberFormats.DOLLAR,
    prefix: '$',
    suffix: undefined,
    thousandsSeparator: NumberSeparators.WHITESPACE,
    decimalSeparator: NumberSeparators.DOT,
    decimalPlaces: undefined
  },
  [NumberFormats.CURRENCY]: {
    format: NumberFormats.CURRENCY,
    prefix: '$',
    suffix: undefined,
    thousandsSeparator: NumberSeparators.WHITESPACE,
    decimalSeparator: NumberSeparators.DOT,
    decimalPlaces: undefined
  }
};

/** A format validator for a number field. */
export const numberFormatValidator = z.object({
  format: z.nativeEnum(NumberFormats).default(NumberFormats.NUMBER),
  minimumFractionDigits: z.number().optional(),
  maximumFractionDigits: z.number().optional(),
  decimalPlaces: z.number().optional(),
  prefix: z.string().optional(),
  suffix: z.string().optional(),
  decimalSeparator: z.nativeEnum(NumberSeparators).default(NumberSeparators.DOT),
  thousandsSeparator: z.nativeEnum(NumberSeparators).optional()
});

/** A configured format for a number field. */
export type NumberFormat = z.infer<typeof numberFormatValidator>;
export type MinimumOrMaximumFractionDigitsType = Extract<
  keyof NumberFormat,
  'minimumFractionDigits' | 'maximumFractionDigits' | 'decimalPlaces'
>;
export type SeparatorFieldType = Extract<
  keyof NumberFormat,
  'decimalSeparator' | 'thousandsSeparator'
>;
export type PrefixOrSuffixFieldType = Extract<keyof NumberFormat, 'prefix' | 'suffix'>;

export interface Hyperlink {
  url: string;
  label?: string;
  /** If true, it means that the label was explicitly typed in by the user, rather than being auto-resolved. */
  isLabelExplicit?: boolean;
}

export interface EntryReference {
  databaseSpaceKey?: string;
  databaseId?: string;
  contentId?: string;
  entryId: string;
  title?: string;
}

export const CUSTOM_TEMPLATE_EMPTY: TemplateReference = {
  templateId: 'empty'
};

export const CUSTOM_TEMPLATE_ENTRY_MACRO: TemplateReference = {
  templateId: 'entry-macro'
};

export enum BacklinkDisplayType {
  LINK = 'link',
  NUMBER = 'number'
}

export const backlinkDisplayTypeValidator = z.nativeEnum(BacklinkDisplayType);

export enum ImportedFileType {
  HTML = 'HTML',
  CSV = 'CSV',
  TEXT = 'TEXT'
}

export enum ExportFileType {
  HTML = 'HTML',
  CSV = 'CSV',
  PDF = 'PDF'
}

export enum RenderContext {
  /** Exporting the database to a CSV/HTML file */
  EXPORT_DATABASE = 'EXPORT_DATABASE',
  /** Copy the database as HTML to the clipboard */
  COPY_DATABASE_TO_CLIPBOARD = 'COPY_DATABASE_TO_CLIPBOARD',
  /** Render the database as HTML for print or export to PDF */
  PRINT_PREVIEW = 'PRINT_PREVIEW'
}

export enum DisplayedDetailType {
  CREATED_AT = 'created_at',
  CREATED_BY = 'created_by',
  LAST_UPDATED_AT = 'last_updated_at',
  LAST_UPDATED_BY = 'last_updated_by',
  EXCERPT = 'excerpt',
  CURRENT_VERSION = 'current_version'
}

export const displayedDetailValidator = z.nativeEnum(DisplayedDetailType);

export const spaceKeyOptionalValidator = transformValidator(
  z.string().optional(),
  (val) => val && { key: val },
  spaceReferenceValidator.optional()
);

/**
 * Validators for the DB_FIELD macro parameters that will be parsed as field settings. All parameters should be
 * optional (for backwards compatibility).
 */
export const fieldSettingsValidator = z.object({
  pageSubType: pageSubTypeValidator.default(PageSubType.LIVE),
  parentPage: z
    .object({
      contentId: z.string()
    })
    .optional(),
  parentPageOption: parentPageOptionValidator.default(ParentPageOption.FIXED),
  isParentPageSourceDatabase: booleanStringValidator(z.boolean().optional()),
  parentPageType: parentPageTypeValidator.default(ParentPageType.PAGE),
  pageTemplate: jsonStringValidator(
    templateReferenceValidator.default(() => CUSTOM_TEMPLATE_EMPTY)
  ),
  displayEntryOnPage: booleanStringValidator(z.boolean().optional()),
  databaseSpace: spaceKeyOptionalValidator,
  databaseId: z.string().optional(),
  multi: booleanStringValidator(z.boolean().default(true)),
  selectOptions: jsonStringValidator(z.array(selectOptionValidator).default(() => [])),
  /**
   * Whether pages/select options can be created through the field. The default is true for select fields
   * and false for page fields. For legacy reasons, the default is true for page fields that have a parent
   * page configured.
   */
  creatable: booleanStringValidator(z.boolean().optional()),
  resolve: booleanStringValidator(z.boolean().default(true)),
  /** A field reference in the same database, for example for a look up field */
  fieldId: z.string().optional(),
  /** For a lookup field, the field in the target database whose value should be shown */
  lookupFieldId: z.string().optional(),
  backlinkDisplayType: backlinkDisplayTypeValidator.default(BacklinkDisplayType.LINK),
  /** For a page labels field, allow editing of the labels */
  allowEdit: booleanStringValidator(z.boolean().default(true)),
  /** For a number field, defines the format */
  numberFormat: jsonStringValidator(
    numberFormatValidator.default(predefinedNumberFormats[NumberFormats.NUMBER])
  ),
  /** @deprecated The server ID of the Jira instance application link. */
  jiraServerId: z.string().optional(),
  /** The name of the Jira field whose value to look up */
  jiraField: z.string().optional(),
  /** The cloud ID of the data source instance */
  cloudId: z.string().optional(),
  /** @deprecated The displayed detail of the page detail field */
  displayedDetail: displayedDetailValidator.default(DisplayedDetailType.CREATED_AT),
  /** The field used for displaying data in the content detail field */
  contentField: z.string().optional(),
  /** Include the version comment for the current version */
  includeVersionComment: booleanStringValidator(z.boolean().optional()),
  contentId: z.string().optional(),
  mediaFormat: mediaFormatValidator.default(MediaFormat.AUTOFILL),
  defaultEnabled: booleanStringValidator(z.boolean().default(false)),
  mentionNotificationEnabled: booleanStringValidator(z.boolean().default(true)),
  dateFormat: z.nativeEnum(DateFormat).optional(),
  timeFormat: z.nativeEnum(TimeFormat).optional(),
  enableDateRange: booleanStringValidator(z.boolean().optional()),
  aiFieldConfig: jsonStringValidator(aiFieldConfigValidator.optional())
});

export const commentThreadValidator = z.object({
  threadId: z.string(),
  isResolved: z.boolean().default(false),

  commentCount: z.number().default(0),
  versionNumber: z.number().default(0)
});

export type DatabaseCommentThread = z.infer<typeof commentThreadValidator>;

export type ActionType =
  | 'copy'
  | 'expand'
  | 'duplicate'
  | 'move'
  | 'move-up'
  | 'add'
  | 'add-above'
  | 'add-below'
  | 'move-down'
  | 'clear-cells'
  | 'delete'
  | 'delete-entry'
  | 'delete-field'
  | 'move-left'
  | 'move-right'
  | 'hide'
  | 'edit-values'
  | 'close'
  | 'ask-rovo';

export type SelectionMode = 'entry' | 'field' | 'value';

export interface PageLabelsDelta {
  add: string[];
  remove: string[];
}
